// import { type Config } from 'tailwindcss';
import { FW_BOLD, FW_MEDIUM, FW_NORMAL, FW_SEMI_BOLD } from '../Lux/typography';

export const extendedTheme = {
  colors: {
    border: 'hsl(var(--border))',
    input: 'hsl(var(--input))',
    ring: 'hsl(var(--ring))',
    background: 'hsl(var(--background))',
    foreground: 'hsl(var(--foreground))',
    primary: {
      DEFAULT: 'hsl(var(--primary))',
      start: 'hsl(var(--primary-start))',
      end: 'hsl(var(--primary-end))',
      foreground: 'hsl(var(--primary-foreground))',
      purple: 'hsl(var(--primary-purple))',
      pstart: 'hsl(var(--primary-purple-start))',
    },
    secondary: {
      DEFAULT: 'hsl(var(--secondary))',
      foreground: 'hsl(var(--secondary-foreground))',
    },
    dark: {
      DEFAULT: 'hsl(var(--dark))',
      foreground: 'hsl(var(--dark-foreground))',
    },
    destructive: {
      DEFAULT: 'hsl(var(--destructive))',
      foreground: 'hsl(var(--semantic-foreground))',
    },
    caution: {
      DEFAULT: 'hsl(var(--caution))',
      foreground: 'hsl(var(--foreground))',
    },
    informative: {
      DEFAULT: 'hsl(var(--informative))',
      foreground: 'hsl(var(--semantic-foreground))',
    },
    warning: {
      DEFAULT: 'hsl(var(--warning))',
      foreground: 'hsl(var(--semantic-foreground))',
    },
    success: {
      DEFAULT: 'hsl(var(--success))',
      foreground: 'hsl(var(--semantic-foreground))',
    },
    muted: {
      DEFAULT: 'hsl(var(--muted))',
      foreground: 'hsl(var(--muted-foreground))',
    },
    accent: {
      DEFAULT: 'hsl(var(--accent))',
      foreground: 'hsl(var(--accent-foreground))',
    },
    popover: {
      DEFAULT: 'hsl(var(--popover))',
      foreground: 'hsl(var(--popover-foreground))',
    },
    card: {
      DEFAULT: 'hsl(var(--card))',
      foreground: 'hsl(var(--card-foreground))',
    },
    light: {
      DEFAULT: 'hsl(var(--text-light))',
    },
    sidebar: {
      DEFAULT: 'hsl(var(--sidebar-background))',
      foreground: 'hsl(var(--sidebar-foreground))',
      primary: 'hsl(var(--sidebar-primary))',
      'primary-foreground': 'hsl(var(--sidebar-primary-foreground))',
      accent: 'hsl(var(--sidebar-accent))',
      'accent-foreground': 'hsl(var(--sidebar-accent-foreground))',
      border: 'hsl(var(--sidebar-border))',
      ring: 'hsl(var(--sidebar-ring))',
    },
  },
  borderRadius: {
    DEFAULT: `var(--radius)`,
    lg: 'calc(var(--radius) * 2)',
    md: `var(--radius)`,
    sm: 'calc(var(--radius) - 2px)',
  },
  keyframes: {
    'accordion-down': {
      from: { height: '0' },
      to: { height: 'var(--radix-accordion-content-height)' },
    },
    'accordion-up': {
      from: { height: 'var(--radix-accordion-content-height)' },
      to: { height: '0' },
    },
    'gradient-x': {
      '0%, 100%': {
        'background-position': '0% 50%',
      },
      '50%': {
        'background-position': '100% 50%',
      },
    },
  },
  animation: {
    'accordion-down': 'accordion-down 0.2s ease-out',
    'accordion-up': 'accordion-up 0.2s ease-out',
    'gradient-x': 'gradient-x 5s ease infinite',
  },
  fontSize: {
    base: ['15px', { lineHeight: '15px', fontWeight: FW_MEDIUM }],
    h1: ['48px', { lineHeight: '48px', fontWeight: FW_SEMI_BOLD }],
    h2: ['30px', { lineHeight: '36px', fontWeight: FW_SEMI_BOLD, letterSpacing: '-0.75%' }],
    h3: ['24px', { lineHeight: '32px', fontWeight: FW_SEMI_BOLD, letterSpacing: '-0.6%' }],
    h4: ['20px', { lineHeight: '28px', fontWeight: FW_SEMI_BOLD, letterSpacing: '-0.5%' }],
    p: ['16px', { lineHeight: '28px', fontWeight: FW_NORMAL }],
    blockquote: ['16px', { lineHeight: '24px', fontWeight: FW_NORMAL, fontStyle: 'italic' }],
    tableHead: ['13px', { lineHeight: '20px', fontWeight: FW_MEDIUM }],
    tableItem: ['14px', { lineHeight: '20px', fontWeight: FW_NORMAL }],
    list: ['16px', { lineHeight: '24px', fontWeight: FW_BOLD }],
    lead: ['20px', { lineHeight: '28px', fontWeight: FW_NORMAL }],
    large: ['18px', { lineHeight: '28px', fontWeight: FW_SEMI_BOLD }],
    small: ['14px', { lineHeight: '14px', fontWeight: FW_MEDIUM }],
    smallMedium: ['14px', { lineHeight: '14px', fontWeight: FW_MEDIUM }],
    regular: ['14px', { lineHeight: '20px', fontWeight: FW_NORMAL }],
    regularMedium: ['14px', { lineHeight: '20px', fontWeight: FW_MEDIUM }],
    subtle: ['14px', { lineHeight: '20px', fontWeight: FW_NORMAL }],
    subtleSemiBold: ['14px', { lineHeight: '20px', fontWeight: FW_SEMI_BOLD }],
    detail: ['12px', { lineHeight: '20px', fontWeight: FW_MEDIUM }],
  },
  fontWeight: {
    r: FW_NORMAL,
    m: FW_MEDIUM,
    sb: FW_SEMI_BOLD,
    b: FW_BOLD,
  },
  boxShadow: {
    sm: '0px 1px 1px 0px hsla(0 0% 0% / 0.15)',
  },
  zIndex: {
    100: '100',
    200: '200',
  },
}; // satisfies Config['theme'];

export const themeSpacing = {
  '0': '0',
  '0.5': '2px',
  '1': '4px',
  '1.5': '6px',
  '2': '8px',
  '2.5': '10px',
  '3': '12px',
  '3.5': '14px',
  '4': '16px',
  '5': '20px',
  '6': '24px',
  '7': '28px',
  '8': '32px',
  '9': '36px',
  '10': '40px',
  '11': '44px',
  '12': '48px',
  '14': '56px',
  '16': '64px',
  '18': '72px',
  '20': '80px',
  '24': '96px',
  '28': '112px',
  '32': '128px',
  '36': '144px',
  '40': '160px',
  '44': '176px',
  '48': '192px',
  '52': '208px',
  '56': '224px',
  '60': '240px',
  '64': '256px',
  '72': '288px',
  '80': '320px',
  '90': '360px',
  '96': '384px',
  '100': '400px',
};
